import { SearchParams, TextTag, Range } from '../ad_campaign/types';
import { FIELDS_BY_KEY, defaultParams } from '../ad_campaign/fields';
import { createListItem, createAddedItem, readAddedItems, readRange } from '../ad_campaign/dom-helpers';
import { InfiniteChoices, ChoiceRecord } from '../ad_campaign/infinite_choices';
import { createChoicesDropdownOverlay } from '../ad_campaign/choices_overlay';

const BODY = '#ad-set-modal-body';

let savedAudienceParams: Partial<SearchParams> | null = null;
let audienceInf: InfiniteChoices | null = null;
let audienceSelectEl: HTMLSelectElement | null = null;
const audienceOverlay = createChoicesDropdownOverlay(() => audienceSelectEl);

function $body(): JQuery {
  return $(BODY);
}

function resetSearchResult(): void {
  $body().find('.adset-audiences-search-result').removeClass('text-danger').removeAttr('data-count').empty();
  $body().find('.adset-audiences-search-button').show();
  $('#adset-next-btn').removeClass('save-audience');
}

// --- Filter-row interactions (scoped to the ad set audience step) ---

function renderField($row: JQuery): void {
  const $items = $row.find('.ads-pool-added-items');
  if (!$items.length) return;
  const $container = $row.find('.ads-pool-add-container');
  const $input = $container.find('.ads-pool-add-input');
  const $addBtn = $container.find('.ads-pool-add-button');

  $items.empty();
  $input.hide();

  const $keywords = $row.find('[data-keywords]');
  if ($keywords.length) {
    const value = String($keywords.val() ?? '').trim();
    if (!value) {
      $addBtn.show();
      return;
    }
    $addBtn.hide();
    const $item = createAddedItem(value, 'far fa-edit');
    $item.on('click', function() {
      $addBtn.hide();
      $items.empty();
      $input.show();
      $keywords.trigger('focus');
    });
    $items.append($item);
    return;
  }

  if ($row.find('[data-range]').length) {
    $addBtn.show();
    const min = String($row.find('[data-range="min"]').val() ?? '').trim();
    const max = String($row.find('[data-range="max"]').val() ?? '').trim();
    if (min === '' || max === '') return;
    const $item = createAddedItem(`${min} - ${max}`, 'far fa-minus-circle');
    $item.on('click', function() {
      $items.empty();
      resetSearchResult();
    });
    $items.append($item);
  }
}

function appendListItem($row: JQuery, text: string, filter: string | null, filterLabel: string | null): void {
  const $item = createListItem(text, filter, filterLabel);
  $item.on('click', function() {
    $(this).remove();
    resetSearchResult();
  });
  $row.find('.ads-pool-added-items').append($item);
}

function addListItem($row: JQuery): void {
  const $container = $row.find('.ads-pool-add-container');
  const $textInput = $container.find('[data-text]');
  const text = String($textInput.val() ?? '').trim();
  if (!text) { $textInput.trigger('focus'); return; }

  if ($row.data('adset-filter') === 'companies'
      && $row.find('.ads-pool-added-items .ads-pool-added-item').length >= 20) {
    window.alert('Maximum 20 companies');
    return;
  }

  const $checked = $container.find('.ads-pool-add-options input[type="radio"]:checked');
  const filter = $checked.length ? String($checked.val()) : null;
  const filterLabel = $checked.length ? $checked.closest('label').text().trim() : null;

  appendListItem($row, text, filter, filterLabel);
  $textInput.val('').trigger('focus');
  resetSearchResult();
}

// --- Reading search params straight from the (container-scoped) DOM ---

function buildSearchParams(forSave = false): SearchParams {
  const params = defaultParams();

  $body().find('.adset-filter-row[data-adset-filter]').each(function() {
    const $row = $(this);
    const key = $row.data('adset-filter') as keyof SearchParams;
    const field = FIELDS_BY_KEY[key];
    if (!field) return;

    switch (field.kind) {
      case 'text-tags':
        (params[field.key] as TextTag[]) = readAddedItems($row).map(({ text, filter }) => ({ text, filter }));
        break;
      case 'string-array':
        (params[field.key] as string[]) = readAddedItems($row).map(({ text }) => text);
        break;
      case 'keywords': {
        const committed = $row.find('.ads-pool-added-items .ads-pool-added-item').length > 0;
        params.keywords = committed ? String($row.find('[data-keywords]').val() ?? '').trim() : '';
        break;
      }
      case 'range': {
        const committed = $row.find('.ads-pool-added-items .ads-pool-added-item').length > 0;
        if (committed) {
          const range = readRange($row);
          const fallback = defaultParams()[field.key] as Range;
          (params[field.key] as Range) = { min: range.min, max: range.max ?? fallback.max };
        } else if (forSave) {
          delete (params as Partial<SearchParams>)[field.key];
        }
        break;
      }
    }
  });

  return params;
}

function ensureAudienceInfinite(): InfiniteChoices | null {
  const selectEl = $body().find('.adset-audience-select')[0] as HTMLSelectElement | undefined;
  if (!selectEl) return null;

  if (audienceInf && (audienceSelectEl !== selectEl || audienceInf.isStale())) {
    destroyAudienceChoices();
  }

  if (!audienceInf) {
    audienceSelectEl = selectEl;
    audienceInf = new InfiniteChoices({
      el: selectEl,
      url: '/audiences',
      placeholder: 'Choose an audience…',
      searchPlaceholder: 'Search audiences…',
      buildLabel: a => (a.audience_count != null ? `${a.name} (${a.audience_count} candidates)` : a.name),
      choicesConfig: { position: 'bottom' },
      onShowDropdown: audienceOverlay.show,
      onHideDropdown: audienceOverlay.hide,
      autoLoad: false,
    });
    audienceInf.init();
  }
  return audienceInf;
}

function destroyAudienceChoices(): void {
  if (audienceInf) {
    audienceOverlay.hide();
    audienceInf.destroy();
    audienceInf = null;
    audienceSelectEl = null;
  }
}

function loadAudiences(selectId?: string): void {
  const inf = ensureAudienceInfinite();
  if (!inf) return;

  if (selectId) {
    const name = $body().attr('data-audience-name') || '';
    inf.selectRecord({ id: selectId, name, search_params: savedAudienceParams ?? {} } as ChoiceRecord);
  } else {
    inf.reload();
  }
}

// --- Reset / restore ---

export function resetAdSetAudienceStep($modal: JQuery): void {
  $modal.find('#adset-existing-audience-section, #adset-new-audience-section, #adset-audience-name-section').addClass('d-none');
  $modal.find('#adset-existing-audience-card, #adset-new-audience-card').removeClass('active-selection');
  $modal.find('.adset-audiences-search-result').removeClass('text-danger').removeAttr('data-count').empty();
  $modal.find('.adset-audiences-search-button').show();
  $modal.find('.adset-filter-row .ads-pool-added-items').empty();
  $modal.find('.adset-filter-row .ads-pool-add-input').hide();
  $modal.find('.adset-filter-row .ads-pool-add-button').show();
  $modal.find('.adset-filter-row [data-range], .adset-filter-row [data-keywords], .adset-filter-row [data-text]').each(function() {
    (this as HTMLInputElement | HTMLTextAreaElement).value = (this as HTMLInputElement | HTMLTextAreaElement).defaultValue;
  });
  $modal.find('.adset-filter-row .ads-pool-add-options input[type="radio"]').each(function() {
    (this as HTMLInputElement).checked = (this as HTMLInputElement).defaultChecked;
  });
  $modal.find('#adset-audience-name-input').val('');
  destroyAudienceChoices();
  $modal.find('.adset-audience-select').val('');
  savedAudienceParams = null;
  $body().removeAttr('data-audience-id').removeAttr('data-audience-name');
  $modal.find('[data-role="adset-summary-audience"]').html('<span class="text-muted">No audience selected</span>');
}

export function restoreAdSetAudienceStep(): void {
  const storedId = $body().attr('data-audience-id');
  if (!storedId) return;

  if ($('#adset-existing-audience-card').hasClass('active-selection')) {
    loadAudiences(storedId);
  } else if ($('#adset-new-audience-card').hasClass('active-selection')) {
    if ($body().find('.adset-audiences-search-result').attr('data-count')) $('#adset-next-btn').addClass('save-audience');
  }
}

// --- Next-button logic for the audience step ---

export function handleAudienceNext(onAdvance: () => void): void {
  const btn = $('#adset-next-btn');
  const selectedAudienceId = audienceInf?.getSelectedId() || '';
  const isExistingCardActive = $('#adset-existing-audience-card').hasClass('active-selection');
  const isNewCardActive = $('#adset-new-audience-card').hasClass('active-selection');

  if (selectedAudienceId) {
    const cached = audienceInf?.getSelected();
    const audienceLabel = cached?.name || selectedAudienceId;
    $body().attr('data-audience-id', selectedAudienceId);
    $body().attr('data-audience-name', audienceLabel);
    savedAudienceParams = cached?.search_params ?? {};
    onAdvance();
    return;
  }

  if (!isExistingCardActive && !isNewCardActive) {
    window.alert('Please choose an existing audience or create a new one before continuing.');
    return;
  }

  if (isExistingCardActive && !selectedAudienceId) {
    window.alert('Please select an audience from the list before continuing.');
    return;
  }

  if (isNewCardActive && !btn.hasClass('save-audience')) {
    window.alert('Please run the search and name your audience before continuing.');
    return;
  }

  if (btn.hasClass('save-audience')) {
    const audienceName = $('#adset-audience-name-input').val()?.toString().trim();
    if (!audienceName) {
      window.alert('Please enter a name for the audience before continuing.');
      return;
    }
    const count = parseInt($body().find('.adset-audiences-search-result').attr('data-count') ?? '', 10);
    const candidates_count = Number.isFinite(count) ? count : 0;

    const currentParams = buildSearchParams(true);

    const savedAudienceId = $body().attr('data-audience-id');
    const savedName = $body().attr('data-audience-name');
    const unchanged = !!savedAudienceId
      && savedName === audienceName
      && JSON.stringify(savedAudienceParams) === JSON.stringify(currentParams);

    if (unchanged) {
      onAdvance();
      return;
    }

    const originalNextHtml = btn.html();
    btn.prop('disabled', true).html(
      '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> &nbsp; Saving...'
    );
    $('#adset-back-btn').prop('disabled', true);

    $.ajax({
      url: savedAudienceId ? `/audiences/${savedAudienceId}` : '/ads_pool_search/save_audience',
      method: savedAudienceId ? 'PATCH' : 'POST',
      contentType: 'application/json',
      headers: { 'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '' },
      data: JSON.stringify({ name: audienceName, data: currentParams, candidates_count: candidates_count }),
      success: function(response: { success: boolean; error?: string; audience_id: string }) {
        if (response.success) {
          window.alert('Audience saved successfully');
          $body().attr('data-audience-id', response.audience_id);
          $body().attr('data-audience-name', audienceName);
          savedAudienceParams = currentParams;
          onAdvance();
        } else {
          window.alert(response.error ?? 'Failed to save audience');
        }
      },
      error: function() {
        window.alert('An error occurred while saving the audience');
      },
      complete: function() {
        btn.prop('disabled', false).html(originalNextHtml);
        $('#adset-back-btn').prop('disabled', false);
      },
    });
  }
}

// --- Event wiring (bound once per turbo:load when the ad set modal exists) ---

export function initAdSetAudience(): void {
  const $modal = $('#adSetModal');
  if (!$modal.length) return;

  $('#adset-existing-audience-card').off('click').on('click', function() {
    $(this).addClass('active-selection');
    $('#adset-new-audience-card').removeClass('active-selection');
    $('#adset-existing-audience-section').removeClass('d-none');
    $('#adset-new-audience-section').addClass('d-none');
    $('#adset-next-btn').removeClass('save-audience');
    loadAudiences();
  });

  $('#adset-new-audience-card').off('click').on('click', function() {
    $(this).addClass('active-selection');
    $('#adset-existing-audience-card').removeClass('active-selection');
    $('#adset-existing-audience-section').addClass('d-none');
    $('#adset-new-audience-section').removeClass('d-none');
    $('#adset-next-btn').addClass('save-audience');
  });

  $(document).off('keypress.adsetListAdd').on('keypress.adsetListAdd', '#adset-page-audience .adset-filter-row [data-text]', function(e) {
    if ((e as JQuery.KeyPressEvent).which !== 13) return;
    e.preventDefault();
    addListItem($(this).closest('.adset-filter-row'));
  });

  $(document).off('click.adsetFilterReveal').on('click.adsetFilterReveal', '#adset-page-audience .adset-filter-row .ads-pool-add-button', function() {
    const $container = $(this).closest('.ads-pool-add-container');
    $(this).hide();
    $container.find('.ads-pool-add-input').show();
    $container.find('[data-text], [data-keywords], [data-range="min"]').first().trigger('focus');
  });

  $(document).off('mousedown.adsetFilterCollapse').on('mousedown.adsetFilterCollapse', function(e) {
    const target = e.target as Node;
    $('#adset-page-audience .adset-filter-row .ads-pool-add-container').each(function() {
      const container = this as HTMLElement;
      const $container = $(container);
      if (!$container.find('[data-text]').length) return;
      const $input = $container.find('.ads-pool-add-input');
      if (!$input.is(':visible')) return;
      if (container === target || container.contains(target)) return;
      $input.hide();
      $container.find('.ads-pool-add-button').show();
    });
  });

  $(document).off('click.adsetFilterSave').on('click.adsetFilterSave', '#adset-page-audience .adset-filter-row .save-btn', function() {
    const $row = $(this).closest('.adset-filter-row');
    renderField($row);
    resetSearchResult();
  });

  $(document).off('click.adsetFilterDelete').on('click.adsetFilterDelete', '#adset-page-audience .adset-filter-row .delete-btn', function() {
    const $row = $(this).closest('.adset-filter-row');
    $row.find('[data-keywords]').val('');
    renderField($row);
    resetSearchResult();
  });

  $modal.find('.adset-audiences-search-button').off('click').on('click', function() {
    const $btn = $(this);
    const $result = $body().find('.adset-audiences-search-result');
    const params = buildSearchParams();
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';

    const originalHtml = $btn.html();
    $btn.prop('disabled', true).removeClass('btn btn-primary').addClass('is-loading').html(
      '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> &nbsp; Searching candidates...'
    );
    $result.removeClass('text-danger').text('');

    $.ajax({
      url: '/ads_pool_search',
      method: 'POST',
      contentType: 'application/json',
      headers: { 'X-CSRF-Token': csrfToken },
      data: JSON.stringify({ data: params }),
      success: function(response: { results_count?: number; error?: string }) {
        if (response.error) {
          $result.addClass('text-danger').text(response.error);
        } else {
          const count = Number(response.results_count) || 0;
          $result.attr('data-count', String(count))
            .html(`<i class="fal fa-check text-success"></i> &nbsp; <strong>${count.toLocaleString()} prospects found</strong>`);
          $btn.hide();
          $('#adset-audience-name-section').removeClass('d-none');
          $('#adset-next-btn').addClass('save-audience');
        }
      },
      error: function(xhr) {
        const msg = xhr.responseJSON?.error ?? 'Search failed';
        $result.addClass('text-danger').text(msg);
      },
      complete: function() {
        $btn.prop('disabled', false).addClass('btn btn-primary').removeClass('is-loading').html(originalHtml);
      },
    });
  });
}
